import { styled } from '@mui/material/styles';
import {
  Control,
  FieldValues,
  DeepMap,
  FieldError,
  Controller,
} from 'react-hook-form';
import { Box, FormControl } from '@mui/material';
import TextInput from './TextInput';
import { useTranslation } from 'react-i18next';
import { matchIsValidTel, MuiTelInput } from 'mui-tel-input';
import validationUtils from '../../services/validationUtils';

const PREFIX = 'PhoneInput';

const classes = {
  phone: `${PREFIX}-phone`,
  country: `${PREFIX}-country`,
  number: `${PREFIX}-number`,
  extension: `${PREFIX}-extension`,
};

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  [`& .${classes.phone}`]: {
    display: 'flex',
  },

  [`& .${classes.country}`]: {
    width: '35%',
  },

  [`& .${classes.number}`]: {
    flexGrow: 1,
  },

  [`& .${classes.extension}`]: {
    width: 100,
    whiteSpace: 'nowrap',
  },
}));

interface PhoneInputProps {
  label: string;
  name: string;
  extensionName: string;
  control: Control;
  required?: boolean;
  errors?: DeepMap<FieldValues, FieldError>;
  defaultValue?: any;
}

export default function PhoneInput({
  label,
  name,
  extensionName,
  control,
  required = false,
  errors = undefined,
  defaultValue = undefined,
}: PhoneInputProps) {
  const { t } = useTranslation('common');

  return (
    <StyledFormControl variant="filled">
      <Box component="div" className={classes.phone}>
        <Controller
          name={name}
          control={control}
          rules={{
            validate: (value) => {
              const isValid =
                value &&
                matchIsValidTel(value, {
                  onlyCountries: ['US', 'CA', 'PR'],
                });
              const isEmpty = !value || value === '+1';
              let errorMessage = undefined;
              if (required && !isValid) {
                errorMessage = t('common.invalidPhone');
              } else if (!isValid && !isEmpty) {
                errorMessage = t('common.invalidPhone');
              }
              return errorMessage;
            },
          }}
          render={({
            field: { ref: fieldRef, value, ...fieldProps },
            fieldState,
          }) => (
            <MuiTelInput
              {...fieldProps}
              required={required}
              value={value ?? ''}
              inputRef={fieldRef}
              onlyCountries={['US', 'CA', 'PR']}
              helperText={fieldState.invalid ? t('common.invalidPhone') : ''}
              error={fieldState.invalid}
              defaultCountry="US"
              label={label}
              className={classes.number}
              variant="filled"
            />
          )}
        />
        {extensionName && (
          <TextInput
            label={t('common.extension')}
            type="number"
            name={extensionName}
            required={false}
            control={control}
            errors={errors}
            defaultValue={defaultValue}
            className={classes.extension}
            rules={{
              pattern: validationUtils.extNumberValidation(),
            }}
            inputProps={{ maxLength: 10, min: 0 }}
          />
        )}
      </Box>
    </StyledFormControl>
  );
}
